/**
 * Accessible tabs for product-tabs section: tablist above panels, keyboard support.
 */
class ProductTabs extends HTMLElement {
  constructor() {
    super();
    /** @param {MouseEvent} e */
    this._onTabClick = (e) => {
      const target = e.target instanceof Element ? e.target.closest('[role="tab"]') : null;
      if (!target || !(target instanceof HTMLElement)) return;
      const index = this.tabs.indexOf(target);
      if (index >= 0) this.selectTab(index);
    };
    /** @param {KeyboardEvent} e */
    this._onKeydown = (e) => {
      this.onKeydown(e);
    };
  }

  connectedCallback() {
    this.tablist = this.querySelector('[role="tablist"]');
    this.tabs = this.tablist ? Array.from(this.tablist.querySelectorAll('[role="tab"]')) : [];
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

    if (this.tabs.length === 0 || this.panels.length === 0) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this._onTabClick);
    });
    this.tablist?.addEventListener('keydown', this._onKeydown);

    this.selectTab(0);
  }

  disconnectedCallback() {
    this.tabs.forEach((tab) => {
      tab.removeEventListener('click', this._onTabClick);
    });
    this.tablist?.removeEventListener('keydown', this._onKeydown);
    this.tablist = null;
    this.tabs = [];
    this.panels = [];
  }

  /**
   * After a tabpanel’s `hidden` state changes, sync carousels once layout is committed.
   * Double rAF defers past the frame that applies visibility.
   * @param {HTMLElement} panel
   */
  _scheduleSliderLayoutRefresh(panel) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        panel.querySelectorAll('slider-component').forEach((/** @type {any} */ host) => {
          if (!host.slider) {
            host.init?.();
            return;
          }
          host.slider.update?.();
          if (typeof host.slider.updateSize === 'function') {
            host.slider.updateSize();
          }
        });
        panel.querySelectorAll('slideshow-component').forEach((/** @type {any} */ host) => {
          // scrollIntoView used host.current, which #sync can mis-read before layout settles
          // (same resource-list carousel as featured blog, but tabs toggle hidden). Force first slide.
          if (typeof host.select === 'function') {
            host.select(0, undefined, { animate: false });
          }
        });
      });
    });
  }

  /**
   * @param {number} index
   */
  selectTab(index) {
    if (index < 0 || index >= this.tabs.length) return;

    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    });

    this.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
      if (i === index) {
        this._scheduleSliderLayoutRefresh(panel);
      }
    });
  }

  /**
   * @param {KeyboardEvent} e
   */
  onKeydown(e) {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') {
      return;
    }

    e.preventDefault();
    const len = this.tabs.length;
    let current = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    if (current < 0) current = 0;

    let next = current;
    if (e.key === 'ArrowRight') {
      next = (current + 1) % len;
    } else if (e.key === 'ArrowLeft') {
      next = (current - 1 + len) % len;
    } else if (e.key === 'Home') {
      next = 0;
    } else if (e.key === 'End') {
      next = len - 1;
    }

    this.selectTab(next);
    this.tabs[next]?.focus();
  }
}

if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabs);
}
